/**
 * 🔗 API Routes Configuration
 *
 * مركز توحيد جميع مسارات API المستخدمة في النظام
 * يجب استخدام هذه المسارات في جميع DataSources
 *
 * Last Updated: عند اكتشاف تضارب المسارات
 */

export const API_ROUTES = {
  // 🔐 Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH_TOKEN: '/auth/refresh',
    LOGOUT: '/auth/logout'
  },

  // 👤 Users Management
  USERS: {
    REGISTER: '/users/register',
    BASE: '/users',
    ADMINS: '/users/admins',
    ME: '/users/me',
    CHANGE_PASSWORD: '/users/change-password',
    BY_ID: (id: string) => `/users/${id}`,
    PROFILE: (id: string) => `/users/${id}/profile`,
    PASSWORD: (id: string) => `/users/${id}/password`,
    ROLE: (id: string) => `/users/${id}/role`,
    ROLES: (id: string) => `/users/${id}/roles`,
    STATISTICS: '/users/statistics',
    DELETE: (id: string) => `/users/${id}`,
    ACTIVATE: (id: string) => `/users/${id}/activate`,
    DEACTIVATE: (id: string) => `/users/${id}/deactivate`,
    PERMISSIONS: {
      LIST: (userId: string) => `/users/${userId}/permissions`,
      REMOVE: (userId: string, permissionId: string) => `/users/${userId}/permissions/${permissionId}`
    }
  },

  // ✅ Checklists (CR09 — Salam dynamic checklist engine)
  CHECKLISTS: {
    BASE: '/checklists',
    SUBMISSIONS: '/checklists/submissions',
    BY_ID: (id: string | number) => `/checklists/${id}`,
    STATUS: (id: string | number) => `/checklists/${id}/status`
  },

  // 🔐 Permissions Management (RBAC)
  PERMISSIONS: {
    BASE: '/permissions',
    BY_ID: (id: string) => `/permissions/${id}`,
    STATISTICS: '/permissions/statistics',
    ROLES: {
      ASSIGN: '/permissions/roles/assign',
      PERMISSIONS: (roleId: string) => `/permissions/roles/${roleId}/permissions`
    },
    USERS: {
      ASSIGN: '/permissions/users/assign',
      DENY: '/permissions/users/deny',
      EFFECTIVE: (userId: string) => `/permissions/users/${userId}/effective-permissions`
    }
  },

  // 👤 Roles Management (RBAC)
  ROLES: {
    BASE: '/roles',
    BY_ID: (id: string) => `/roles/${id}`,
    STATISTICS: '/roles/statistics',
    PERMISSIONS: {
      // 1. Assign Permission to Role
      ASSIGN: (roleId: string) => `/roles/${roleId}/permissions`,
      // 2. Get Role Permissions
      LIST: (roleId: string) => `/roles/${roleId}/permissions`,
      // 3. Remove Permission from Role
      REMOVE: (roleId: string, permissionId: string) => `/roles/${roleId}/permissions/${permissionId}`,
      // 4. Update Role Permission Assignment
      UPDATE: (roleId: string, permissionId: string) => `/roles/${roleId}/permissions/${permissionId}`,
      // 5. Get Roles by Permission
      BY_PERMISSION: (permissionId: string) => `/roles/by-permission/${permissionId}`,
      // 6. Bulk Assign Permissions to Role
      BULK_ASSIGN: (roleId: string) => `/roles/${roleId}/permissions/bulk`,
      // 7. Bulk Remove Permissions from Role
      BULK_REMOVE: (roleId: string) => `/roles/${roleId}/permissions/bulk`
    },
    USERS: {
      ASSIGN: '/roles/users/assign',
      LIST: (roleId: string) => `/roles/${roleId}/users`
    }
  },

  // 👥 Marketers (المسوقين)
  MARKETERS: {
    BASE: '/users/marketers',           // New endpoint for creating marketers
    MARKET_DEVELOPERS: '/users/market-developers',  // Legacy endpoint
    NEARBY: '/users/nearby-marketers'
  },

  // 🏪 Stores (المتاجر)
  STORES: {
    BASE: '/stores',
    ALL: '/stores/all',
    BY_ID: (id: string) => `/stores/${id}`,
    GEO_LOCATION: (id: string) => `/stores/${id}/geo-location`,
    MAP: '/stores/map',
    NEARBY: '/stores/nearby',
    ADD_DEBT: (storeId: string) => `/stores/${storeId}/add-debt`,
    STATISTICS: '/stores/statistics',
    // Store Deliveries Ledger (see STORE_DELIVERIES for spec-aligned routes)
    DELIVERIES_SUMMARY: (storeId: string) => `/store-deliveries/summary?store_id=${storeId}`,
    DELIVERIES_TIMELINE: (storeId: string) => `/store-deliveries/timeline?store_id=${storeId}`,
    DELIVERY_TRANSACTION: (transactionId: string) => `/store-deliveries/transactions/${transactionId}`
  },

  // Store Deliveries (spec: GET /api/v1/dashboard/store-deliveries)
  STORE_DELIVERIES: {
    LIST: '/store-deliveries',
    TIMELINE: '/store-deliveries/timeline',
    SUMMARY: '/store-deliveries/summary',
    BY_ID: (id: string) => `/store-deliveries/${id}`
  },

  // 📱 Products (المنتجات)
  PRODUCTS: {
    BASE: '/products',
    BY_ID: (id: string) => `/products/${id}`,
    STATS: '/products/stats',

    // Brands (البراندات)
    BRANDS: '/brands',
    BRAND_BY_ID: (id: string) => `/brands/${id}`,

    // Tags (العلامات)
    TAGS: '/tags',
    TAG_BY_ID: (id: string) => `/tags/${id}`,

    // Product Types (أنواع المنتجات)
    TYPES: '/product-types',
    TYPE_BY_ID: (id: string) => `/product-types/${id}`
  },

  // 🏢 Service Providers (مقدمي الخدمات)
  SERVICE_PROVIDERS: {
    BASE: '/provider/providers',
    BY_ID: (id: string) => `/provider/providers/${id}`,
    STATS: (id: string) => `/provider/providers/${id}/stats`,
    ASSIGN_BRAND: '/provider/brands/assign',
    UNASSIGN_BRAND: '/provider/brands/unassign',
    GET_PROVIDER_BRANDS: '/provider/brands',
    GET_MY_BRANDS: '/provider/my-brands',
    GET_BRAND_STORES: (brandId: string) => `/provider/brands/${brandId}/stores`,
    // Provider-specific endpoints (for logged-in provider)
    // Note: Base URL already includes /dashboard, so routes are relative to /dashboard
    MY_NEARBY_STORES: '/provider/stores/nearby',
    MY_VISITS: '/provider/visits',
    BRAND_STORES: (brandId: string) => `/provider/brands/${brandId}/stores`,
    BRAND_STORES_NEARBY: (brandId: string) => `/provider/brands/${brandId}/stores/nearby`,
    BRAND_ANALYTICS: (brandId: string) => `/provider/brands/${brandId}/analytics`,
    STORE_BRAND_DETAILS: (storeId: string, brandId: string) => `/provider/stores/${storeId}/brands/${brandId}`
  },

  // 🚶‍♂️ Visits (الزيارات)
  VISITS: {
    BASE: '/visits',
    BY_ID: (id: string) => `/visits/${id}`,

    // Core Visit Operations
    CREATE: '/visits',
    UPDATE_PRODUCT_QUANTITY: (visitId: string) => `/visits/${visitId}/products/quantity`,
    ADD_IMAGE: (visitId: string) => `/visits/${visitId}/images`,
    COMPLETE: (visitId: string) => `/visits/${visitId}/complete`,
    DELETE: (visitId: string) => `/visits/${visitId}`,
    DELETE_ALL_ACTIVE: '/visits/active/all',

    // Store-specific Operations
    STORE_HISTORY: (storeId: string) => `/visits/store/${storeId}/history`,

    // Dashboard Management Endpoints (aligned with backend API)
    OVERVIEW: '/visits/analytics/trends',
    LIST: '/visits',
    DETAILS: (id: string) => `/visits/${id}`,
    ANALYTICS: '/visits/analytics/trends',
    REPORTS: '/visits/reports',
    APPROVE: (id: string) => `/visits/${id}/approve`,
    REJECT: (id: string) => `/visits/${id}/reject`,
    EDIT: (id: string) => `/visits/${id}/edit`
  },

  // 📍 Location Tracking (تتبع المسوّقين) — GO client only
  LOCATION_TRACKING: {
    SESSIONS: '/location-tracking/sessions',
    SESSION_DETAILS: (id: string) => `/location-tracking/sessions/${id}`,
    SESSION_TRACK: (id: string) => `/location-tracking/sessions/${id}/track`,
    MARKETER_SESSIONS: (marketerId: string) =>
      `/location-tracking/marketers/${marketerId}/sessions`
  },

  // 📦 Inventory (المخزون)
  INVENTORY: {
    BASE: '/inventory',
    SERIAL_DETAILS: (serialNumber: string) => `/inventory/serials/${encodeURIComponent(serialNumber)}`,
    SERIAL_TIMELINE: (serialNumber: string) => `/inventory/serials/${encodeURIComponent(serialNumber)}/timeline`,
    BY_ID: (id: string) => `/inventory/${id}`,
    ADD_STOCK: '/inventory/add-stock',
    MOVEMENTS: '/inventory/movements',
    TRANSFER_QUANTITY: '/inventory/transfer/quantity',
    TRANSFER_SERIAL: '/inventory/transfer/serial',
    PRODUCT_INVENTORY_STATUS: '/inventory/product-status',
    PRODUCTS_INVENTORY_STATUS: '/inventory/products-inventory-status',
    STATISTICS: '/inventory/statistics',
    COMPREHENSIVE_REPORT: '/inventory/comprehensive-report'
  },

  // 🔢 Serials (السيريالات) – single base path for all serial endpoints
  SERIALS: {
    BASE: '/serials',
    BY_ID: (id: string) => `/serials/${id}`,
    STATISTICS: '/serials/statistics',
    ASSIGN: '/serials/assign',
    CHECK: '/serials/check',
    HISTORY: (id: string) => `/serials/${id}/history`
  },

  // 🚚 Distribution (التوزيع)
  DISTRIBUTION: {
    HISTORY: '/distribution/history',
    DELIVERY: '/distribution/delivery', // Migrated from /marketer-products/deliver
    DELIVERY_BY_ID: (id: string) => `/distribution/delivery/${id}`,
    DELETE_TRANSACTION: (transactionId: string) => `/distribution/transactions/${transactionId}`,
    MARKETERS: '/users/market-developers',
    MARKETERS_ALL: '/distribution/marketers/all',
    ALL_SERIALS: '/serials',
    AVAILABLE_SERIALS_BY_PRODUCT: (productId: string) => `/serials/product/${productId}?status=available&location_type=warehouse`,
    AVAILABLE_PRODUCTS: '/distribution/products',
    PRODUCTS_ALL: '/distribution/products/all'
    // TODO: Migrate GET /dashboard/marketer-products/transactions to distribution history endpoint if desired
    // Current: GET /dashboard/marketer-products/transactions (kept unchanged for now)
    // Future: Consider using /distribution/history with appropriate filters
  },

  // 💰 Billing (الفواتير)
  BILLING: {
    BASE: '/billing',
    INVOICES: '/billing/invoices',
    INVOICE_BY_ID: (id: string) => `/billing/invoices/${id}`,
    INVOICE_BY_NUMBER: (invoiceNumber: string) => `/sales/invoices/number/${invoiceNumber}`,
    INVOICE_ATTACHMENT: (id: string) => `/billing/invoices/${id}/attachment`,
    PAYMENTS: '/billing/payments',
    STATISTICS: '/billing/statistics',
    STORE_DEBTS: '/billing/store-debts'
  },

  // 💼 Sales (المبيعات)
  SALES: {
    BASE: '/sales',
    ALL: '/sales/all',
    STATISTICS: '/sales/statistics',
    BY_ID: (id: string) => `/sales/${id}`,
    INVOICES: '/sales/invoices',
    INVOICE_STATUS: (id: string | number) => `/sales/invoices/${id}/status`,
    STORE_SALES: (storeId: string | number) => `/sales/store/${storeId}`,
    STORE_OUTSTANDING: (storeId: string | number) => `/sales/store/${storeId}/outstanding`,
    PAYMENTS_RECORD: '/sales/payments/record',
    RECORD_PAYMENT: '/sales/payments/record',
    CHECK_OVERDUE: '/sales/check-overdue-invoices',
    UPDATE_COLLECTION_STAGES: '/sales/update-collection-stages',
    PENDING_BANK_TRANSFERS: '/sales/pending-bank-transfers',
    REVIEW_BANK_TRANSFER: '/sales/review-bank-transfer',
    AGING_REPORT: '/sales/aging-report'
  },

  // 💳 Credit (Dashboard Credit Endpoints)
  CREDIT: {
    AVAILABLE_CREDIT: (storeId: string | number) => `/credit/store/${storeId}/available-credit`,
    CREDIT_ELIGIBILITY: (storeId: string | number) => `/credit/store/${storeId}/credit-eligibility`,
    UPDATE_RATING: (storeId: string | number) => `/credit/store/${storeId}/update-rating`,
    CREDIT_REPORT: (storeId: string | number) => `/credit/store/${storeId}/credit-report`,
    CREDIT_REPORTS: '/credit/reports',
    UPDATE_CREDIT_LIMIT: (storeId: string | number) => `/credit/store/${storeId}/credit-limit`
  },


  // 🔒 Security (الأمان)
  SECURITY: {
    CSRF_TOKEN: '/api/csrf-token'
  },

  // 🗺️ Locations (المواقع)
  LOCATIONS: {
    BASE: '/locations',
    BY_ID: (id: string) => `/locations/${id}`
  },

  // 📊 Reports (التقارير)
  REPORTS: {
    BASE: '/reports',
    MARKETERS: '/reports/marketers',
    MARKETER_STORES: (marketerId: string | number) => `/reports/marketer/${marketerId}/stores-report`,
    MARKETER_FINANCIAL: (marketerId: string | number) => `/reports/marketer/${marketerId}/financial-report`,
    MARKETER_EXPORT: (marketerId: string | number) => `/reports/marketer/${marketerId}/export`,
    MARKETER_STOCK: (marketerId: string | number) => `/reports/marketer/${marketerId}/stock`,
    PRODUCTS_SALES: '/reports/products-sales'
  }
} as const;

/**
 * 📋 Type Definitions for API Routes
 * لضمان Type Safety عند استخدام المسارات
 */
export type ApiRoutes = typeof API_ROUTES;

/**
 * 🛠️ Helper Functions
 */
export const ApiUtils = {
  /**
   * بناء URL مع query parameters
   */
  buildUrl: (baseUrl: string, params?: Record<string, unknown>): string => {
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },

  /**
   * التحقق من صحة المسار
   */
  isValidRoute: (route: string): boolean => {
    return route.startsWith('/') && route.length > 1;
  }
};

